import type { JSX } from 'react';
import { Link, Navigate } from 'react-router';
import { useBrand } from '../brand/context.js';
import { useLegal } from '../brand/legal-context.js';
import { useI18n } from '../i18n/context.js';

/**
 * The complaint channel: how someone who is not a user tells us that a published book should
 * not be published (`docs/abuse-response.md` §3.5, §6.4).
 *
 * The address comes from the brand, not from this page: the process is the same in both
 * deployments and it is the address that differs, so the page is shared and the value is not.
 * An empty address means the deployment has no channel, and the page redirects rather than
 * publishing a mailbox that does not exist.
 *
 * The preservation promise in these texts is a commitment, not decoration: it is only true while
 * `docs/abuse-response.md` §3.1–3.3 — quarantine, retention flag, destruction record — are also
 * implemented. No deadline of ours is published here. The only deadline that binds is the 48 hours
 * in the terms, which is 47 U.S.C. § 223a's own; the 24-hour acknowledgement is an internal target
 * in `docs/abuse-procedure.md` §0, and `packages/web-core/test/legal.test.tsx` fails if one of
 * ours reappears in published text.
 */
export function ReportPage(): JSX.Element {
  const { t } = useI18n();
  const legal = useLegal();
  const { abuse } = useBrand();

  if (!abuse.email) return <Navigate to="/" replace />;

  return (
    <main className="mx-auto min-h-screen max-w-3xl px-4 py-8 sm:py-12">
      <Link className="text-sm underline" to="/">
        {legal.ui.backHome}
      </Link>
      <article className="kitup-card mt-5 p-5 sm:p-8 [&_a]:underline [&_h2]:mt-8 [&_h2]:text-xl [&_h2]:font-semibold [&_li]:leading-7 [&_p]:mt-3 [&_p]:leading-7 [&_ul]:mt-3 [&_ul]:list-disc [&_ul_li]:ml-5">
        <h1 className="text-3xl font-semibold">{t.report.title}</h1>
        <p>{t.report.intro}</p>

        <section>
          <h2>{t.report.howTitle}</h2>
          <p>{t.report.howText}</p>
          <p className="text-lg font-semibold">
            <a href={`mailto:${abuse.email}`}>{abuse.email}</a>
          </p>
        </section>

        <section>
          <h2>{t.report.nextTitle}</h2>
          <ul>
            {t.report.next.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>
        </section>

        <section>
          <h2>{t.report.responseTitle}</h2>
          <p>{t.report.responseText}</p>
        </section>

        <section>
          <h2>{t.report.privacyTitle}</h2>
          <p>{t.report.privacyText}</p>
        </section>
      </article>
    </main>
  );
}
